import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  deleteV1CompaniesCompanyIdJobsId,
  getV1CompaniesCompanyIdJobs,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_UUID } from '~hooks/useCompany/useCompany';
import { Job } from '~hooks/useJob/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

interface JobResult {
  jobId: string;
  job: Job | null;
  status: 'pending' | 'found' | 'not_found' | 'deleting' | 'deleted' | 'failed';
  error?: string;
}

const DELAY_BETWEEN_REQUESTS_MS = 500;

export function parseJobIds(text: string): string[] {
  const matches = text.match(/TRD-\d+/gi) ?? [];
  const unique = [...new Set(matches.map((id) => id.toUpperCase()))];
  return unique.sort((a, b) => {
    const numA = parseInt(a.replace('TRD-', ''));
    const numB = parseInt(b.replace('TRD-', ''));
    return numA - numB;
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const BulkDeleteJobUtility = () => {
  const { toasterStore } = useStores();
  const abortRef = useRef(false);

  const [pasteValue, setPasteValue] = useState('');
  const [parsedIds, setParsedIds] = useState<string[]>([]);
  const [results, setResults] = useState<JobResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handlePasteChange = (value: string) => {
    setPasteValue(value);
    const ids = parseJobIds(value);
    setParsedIds(ids);
    setResults([]);
  };

  const removeId = (idToRemove: string) => {
    setParsedIds((prev) => prev.filter((id) => id !== idToRemove));
    setResults((prev) => prev.filter((r) => r.jobId !== idToRemove));
  };

  const handleSearchAll = useCallback(async () => {
    if (parsedIds.length === 0) return;

    setIsSearching(true);
    const newResults: JobResult[] = parsedIds.map((id) => ({
      jobId: id,
      job: null,
      status: 'pending',
    }));
    setResults(newResults);

    try {
      for (let i = 0; i < parsedIds.length; i++) {
        try {
          const response = await getV1CompaniesCompanyIdJobs({
            path: { 'company-id': TREAD_UUID },
            query: { 'search[datagrid]': parsedIds[i], 'page[limit]': 10 },
          });
          const rawMatch = response.data.data.find(
            (j) => j.job_id.toUpperCase() === parsedIds[i],
          );
          const match = rawMatch ? Job.parse(rawMatch) : null;
          newResults[i] = {
            ...newResults[i],
            job: match,
            status: match ? 'found' : 'not_found',
          };
        } catch (error) {
          try {
            connection.handleRequestError(
              error,
              t('administration.utilities.failed_to_search'),
            );
          } catch {
            // handleRequestError throws; continue searching remaining IDs
          }
          newResults[i] = { ...newResults[i], status: 'not_found' };
        }
        setResults([...newResults]);

        if (i < parsedIds.length - 1) {
          await delay(DELAY_BETWEEN_REQUESTS_MS);
        }
      }
    } finally {
      setIsSearching(false);
    }
  }, [parsedIds]);

  const deletableResults = results.filter((r) => r.status === 'found' && r.job?.company);

  const handleBulkDelete = async () => {
    abortRef.current = false;
    setIsDeleting(true);
    setIsConfirmOpen(false);

    const toDelete = [...deletableResults];
    setDeleteProgress({ current: 0, total: toDelete.length });

    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < toDelete.length; i++) {
        if (abortRef.current) break;

        const item = toDelete[i];
        setDeleteProgress({ current: i + 1, total: toDelete.length });

        setResults((prev) =>
          prev.map((r) => (r.jobId === item.jobId ? { ...r, status: 'deleting' } : r)),
        );

        try {
          await deleteV1CompaniesCompanyIdJobsId({
            path: { 'company-id': item.job!.company!.id, id: item.job!.id },
          });
          setResults((prev) =>
            prev.map((r) => (r.jobId === item.jobId ? { ...r, status: 'deleted' } : r)),
          );
          successCount++;
        } catch (error) {
          try {
            connection.handleRequestError(error);
          } catch {
            // handleRequestError throws; continue deleting remaining jobs
          }
          setResults((prev) =>
            prev.map((r) => (r.jobId === item.jobId ? { ...r, status: 'failed' } : r)),
          );
          failCount++;
        }

        if (i < toDelete.length - 1) {
          await delay(DELAY_BETWEEN_REQUESTS_MS);
        }
      }

      const wasAborted = abortRef.current;
      const skippedCount = toDelete.length - successCount - failCount;

      if (wasAborted) {
        toasterStore.push(
          alert(
            t('administration.utilities.bulk_delete_cancelled', {
              success: successCount,
              failed: failCount,
              skipped: skippedCount,
            }),
            AlertTypes.warn,
          ),
        );
      } else {
        toasterStore.push(
          alert(
            t('administration.utilities.bulk_delete_complete', {
              success: successCount,
              failed: failCount,
            }),
            failCount > 0 ? AlertTypes.warn : AlertTypes.success,
          ),
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAbort = () => {
    abortRef.current = true;
  };

  const progressPercent =
    deleteProgress.total > 0 ? (deleteProgress.current / deleteProgress.total) * 100 : 0;

  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 700 }}>
      <TextField
        multiline
        minRows={3}
        maxRows={6}
        fullWidth
        placeholder={t('administration.utilities.bulk_paste_hint')}
        value={pasteValue}
        onChange={(e) => handlePasteChange(e.target.value)}
        disabled={isSearching || isDeleting}
        sx={{ mb: 2 }}
      />
      {parsedIds.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {parsedIds.map((id) => {
            const result = results.find((r) => r.jobId === id);
            const color = getChipColor(result?.status);
            return (
              <Chip
                key={id}
                label={id}
                size="small"
                color={color}
                deleteIcon={<CancelIcon />}
                onDelete={isSearching || isDeleting ? undefined : () => removeId(id)}
              />
            );
          })}
        </Box>
      )}
      {parsedIds.length > 0 && results.length === 0 && (
        <SmallLoadingButton
          variant="contained"
          loading={isSearching}
          onClick={handleSearchAll}
        >
          {t('administration.utilities.bulk_search_all')} ({parsedIds.length})
        </SmallLoadingButton>
      )}
      {results.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th, & td': {
                textAlign: 'left',
                p: 0.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
                fontSize: '0.875rem',
              },
              '& th': { fontWeight: 600, color: 'text.secondary' },
            }}
          >
            <thead>
              <tr>
                <th>{t('form_fields.job')}</th>
                <th>{t('administration.utilities.job_company')}</th>
                <th>{t('administration.utilities.customer_account')}</th>
                <th>{t('administration.utilities.job_state')}</th>
                <th>{t('administration.utilities.status')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.jobId}>
                  <td>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      {r.jobId}
                    </Typography>
                  </td>
                  <td>{r.job?.company?.legalName ?? '—'}</td>
                  <td>{r.job?.order?.account?.name ?? '—'}</td>
                  <td>{r.job ? <Chip label={r.job.status} size="small" /> : '—'}</td>
                  <td>
                    <StatusLabel status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>

          {isDeleting && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                {t('administration.utilities.bulk_delete_progress', {
                  current: deleteProgress.current,
                  total: deleteProgress.total,
                })}
              </Typography>
              <LinearProgress variant="determinate" value={progressPercent} />
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            {!isDeleting && !isSearching && deletableResults.length > 0 && (
              <SmallButton
                color="brandV2Red"
                variant="contained"
                onClick={() => setIsConfirmOpen(true)}
              >
                {t('administration.utilities.bulk_delete_jobs')} (
                {deletableResults.length})
              </SmallButton>
            )}
            {isDeleting && (
              <SmallButton variant="outlined" color="secondary" onClick={handleAbort}>
                {t('actions.cancel')}
              </SmallButton>
            )}
          </Box>
        </Box>
      )}
      <ConfirmationDialog
        isBusy={isDeleting}
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleBulkDelete}
        title={t('administration.utilities.bulk_delete_jobs')}
      >
        <Typography>
          {t('administration.utilities.bulk_confirm_delete', {
            count: deletableResults.length,
          })}
        </Typography>
        <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
          {deletableResults.map((r) => (
            <Typography
              key={r.jobId}
              variant="body2"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {r.jobId}
            </Typography>
          ))}
        </Box>
      </ConfirmationDialog>
    </Paper>
  );
};

function getChipColor(
  status?: JobResult['status'],
): 'default' | 'success' | 'error' | 'warning' | 'info' {
  switch (status) {
    case 'found':
      return 'success';
    case 'not_found':
      return 'error';
    case 'deleted':
      return 'success';
    case 'failed':
      return 'error';
    case 'deleting':
      return 'info';
    default:
      return 'default';
  }
}

function StatusLabel({ status }: { status: JobResult['status'] }) {
  switch (status) {
    case 'pending':
      return <Typography variant="body2">...</Typography>;
    case 'found':
      return (
        <Chip
          label={t('administration.utilities.status_ok')}
          size="small"
          color="success"
        />
      );
    case 'not_found':
      return (
        <Chip
          label={t('administration.utilities.bulk_not_found')}
          size="small"
          color="error"
        />
      );
    case 'deleting':
      return <Typography variant="body2">...</Typography>;
    case 'deleted':
      return (
        <Chip
          label={t('administration.utilities.status_deleted')}
          size="small"
          color="success"
        />
      );
    case 'failed':
      return (
        <Chip
          label={t('administration.utilities.status_failed')}
          size="small"
          color="error"
        />
      );
    default: {
      const exhaustive: never = status;
      throw new Error(`Unhandled status: ${exhaustive}`);
    }
  }
}
